import React, {
  useCallback, useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import PropTypes from 'prop-types';
import IcomoonReact from 'icomoon-react';

import iconSet from '../../../../shared/images/teambuildr-selection.json';
import hexToRgba from '../../../../shared/utils/hexToRgba';
import { SUPERSET_DEFAULTS, normalizeSupersetColor } from '../../../../shared/constants';
import { claimArrowKeys, hasArrowKeyOwner } from '../../utils/arrowKeyOwnership';

/**
 * Superset grouping picker sized to sit inline next to a table's sets stepper —
 * a keyboard-friendly alternative to the kebab quick action.
 *
 * Presentational only: it reads the current grouping from props and reports a
 * choice through `onChoose`. Persistence deliberately lives with the host,
 * because it differs — a pre-save table only needs Redux (the grouping rides
 * along when the item itself saves), while a saved item has to reach the
 * server.
 *
 * Extracted from PreSaveTableTransposed, which still carries its own copy along
 * with the SAQC and Circuit pre-save tables. Those should migrate here.
 */

const SupersetWrapper = styled('div')`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const SupersetButton = styled('button')`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 8px 0 6px;
  /** A control beside the sets stepper, so it takes the same field tier. */
  background: ${({ theme }) => theme.colors.surfaceInput};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 4px;
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  &:focus { outline: none; }
  &:focus-visible { outline: none; box-shadow: 0 0 0 2px #BFDBFE; }
`;

const SupersetSwatch = styled('div')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 4px;
  background: ${(props) => props.background || props.theme.colors.surfaceSecondary};
  color: ${({ theme }) => theme.colors.text};
  font-size: 13px;
  font-weight: 600;
`;

/** Empty-state placeholder for the trigger. Plain muted text instead of a
 * tinted swatch — reads as "nothing here yet" rather than looking like a
 * filled chip. */
const SupersetEmptyHint = styled('span')`
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 2px;
  font-family: 'Nunito Sans';
  font-size: 13px;
  /** Same grey as the Add Property trigger's AddPropertyShortcut (body text at
   * 60%), so the two hints on the row read as one. */
  color: ${({ theme }) => theme.colors.text};
  opacity: 0.6;
`;

/** Portaled to <body> so it stacks above the table/list virtualization (which
 * carries its own z-index up to ~2000 inside the calendar column) and any
 * sibling row elements. Anchored under the trigger via `position: fixed` +
 * measured coords. */
const SupersetPopover = styled('div')`
  position: fixed;
  z-index: 1100;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 10px;
  width: 220px;
  /** Floating, so it sits on the elevated tier above the table. */
  background: ${({ theme }) => theme.colors.surfaceElevated};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
`;

const SupersetChip = styled('button')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 4px;
  /** Transparent at rest so the elevated popover shows through. */
  background: ${(props) => props.background || 'transparent'};
  border: 2px solid ${({ isSelected, theme }) => (isSelected ? theme.colors.link : theme.colors.borderLight)};
  font-family: 'Nunito Sans';
  font-size: 13px;
  font-weight: 600;
  color: ${({ muted, theme }) => (muted ? theme.colors.textSecondary : theme.colors.text)};
  cursor: pointer;
  &:hover { border-color: ${({ isSelected, theme }) => (isSelected ? theme.colors.link : theme.colors.textSecondary)}; }
  &:focus { outline: none; }
  &:focus-visible { outline: none; box-shadow: 0 0 0 2px #BFDBFE; }
`;

/** Arrow-key focus steps inside the popover's 6-column grid. */
const SUPERSET_ARROW_STEPS = {
  ArrowRight: 1,
  ArrowLeft: -1,
  ArrowDown: 6,
  ArrowUp: -6,
};

const POPOVER_WIDTH = 220;
const VIEWPORT_PAD = 8;
/** Gap between the trigger and the popover, on whichever side it opens. */
const POPOVER_OFFSET = 4;
/** Stand-in height for the one frame before the popover has been measured:
 * five rows of 28px chips, 6px gaps, 10px padding and a 1px border. */
const POPOVER_EST_HEIGHT = 186;

/** A–Z plus a clear option. Module scope — the list is fixed, so rebuilding it
 * per render only churned the effect that indexes into it. */
const SUPERSET_OPTIONS = [
  { letter: null, color: null },
  ...Object.entries(SUPERSET_DEFAULTS).map(([letter, color]) => ({ letter, color })),
];

const InlineSupersetPicker = ({
  groupingLetter,
  groupingColorCode,
  onChoose,
  triggerAriaLabel,
}) => {
  const { colors } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  /** Fixed-position coords measured from the trigger's rect. null until open. */
  const [popoverPos, setPopoverPos] = useState(null);
  const [focusIndex, setFocusIndex] = useState(0);
  const buttonRef = useRef(null);
  const popoverRef = useRef(null);
  const chipRefs = useRef([]);

  const choose = useCallback((letter, color) => {
    setIsOpen(false);
    onChoose(letter, color);
    /** Return focus to the trigger after dismissing the popover. */
    setTimeout(() => buttonRef.current?.focus(), 0);
  }, [onChoose]);

  /** Type-to-select: a letter A–Z picks that grouping directly, and
   * Backspace / Delete / 0 clears it. Works whether the trigger is focused or
   * the popover is open. Returns true when handled so callers can
   * preventDefault. */
  const selectByKey = useCallback((e) => {
    /** Modifier combos are app or browser shortcuts — Cmd+I opens Additional
     * Info, Cmd+D deletes — never a grouping pick. Let them through so the
     * shortcut owner handles them and focus stays where that owner put it. */
    if (e.metaKey || e.ctrlKey || e.altKey) return false;
    const { key } = e;
    if (key === 'Backspace' || key === 'Delete' || key === '0') {
      choose(null, null);
      return true;
    }
    if (/^[a-zA-Z]$/.test(key)) {
      const letter = key.toUpperCase();
      const color = SUPERSET_DEFAULTS[letter];
      if (color) {
        choose(letter, color);
        return true;
      }
    }
    return false;
  }, [choose]);

  /** Outside-click + Escape to close. */
  useEffect(() => {
    if (!isOpen) return undefined;
    const onMouseDown = (e) => {
      if (popoverRef.current?.contains(e.target)) return;
      if (buttonRef.current?.contains(e.target)) return;
      setIsOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        /** This popover owns Escape while it's open. Without stopping it here
         * the MonthEditPopover window listener also fires and closes the whole
         * exercise entry behind it. */
        e.stopPropagation();
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen]);

  /** Anchor the portaled popover to the trigger. Measured on open and again on
   * resize — a scroll dismisses the popover instead of moving it. Clamps if it
   * would overflow the viewport.
   *
   * Opens downward by preference and flips up when the viewport has no room
   * below. The picker now sits in the table footer, so on the last table on
   * screen "below the trigger" is off the bottom of the window — the chips were
   * running past the edge and across the stats bar. */
  const computePos = useCallback(() => {
    const btn = buttonRef.current;
    if (!btn) return null;
    const rect = btn.getBoundingClientRect();
    let { left } = rect;
    if (left + POPOVER_WIDTH > window.innerWidth - VIEWPORT_PAD) {
      left = Math.max(VIEWPORT_PAD, window.innerWidth - POPOVER_WIDTH - VIEWPORT_PAD);
    }

    /** Measured, not assumed: the popover mounts hidden at -9999 on the frame
     * before this runs, which still lays it out, so the real height is readable
     * by the time we place it. */
    const height = popoverRef.current?.offsetHeight || POPOVER_EST_HEIGHT;
    const below = rect.bottom + POPOVER_OFFSET;
    const above = rect.top - POPOVER_OFFSET - height;

    let top;
    if (below + height <= window.innerHeight - VIEWPORT_PAD) {
      top = below;
    } else if (above >= VIEWPORT_PAD) {
      top = above;
    } else {
      /** Neither side fits — a viewport too short for the grid either way.
       * Sit as low as it can go rather than committing to a side that clips. */
      top = Math.max(VIEWPORT_PAD, window.innerHeight - VIEWPORT_PAD - height);
    }

    return { top, left };
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return undefined;
    setPopoverPos(computePos());
    /** Scroll dismisses rather than re-anchors. The popover is portaled and
     * fixed, so tracking the trigger meant it slid across the calendar while the
     * list moved under it — and having picked a side to open on, a scroll can
     * invalidate that choice mid-gesture. Capture phase because the calendar
     * list scrolls in its own container, not the window. */
    const onScroll = () => setIsOpen(false);
    const onResize = () => {
      const next = computePos();
      if (!next) return;
      setPopoverPos((prev) => {
        if (prev && prev.top === next.top && prev.left === next.left) return prev;
        return next;
      });
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [isOpen, computePos]);

  /** Own the arrow keys while open — they move chip focus here, and
   * useCalendarKeyboardScroll would otherwise scroll the calendar a row
   * underneath at the same time. */
  useEffect(() => {
    if (!isOpen) return undefined;
    return claimArrowKeys();
  }, [isOpen]);

  /** Land focus on the currently-selected chip (or the clear option if none)
   * when the popover opens, so keyboard users start somewhere obvious. */
  useEffect(() => {
    if (!isOpen) return;
    const idx = groupingLetter
      ? SUPERSET_OPTIONS.findIndex((o) => o.letter === groupingLetter)
      : 0;
    const safeIdx = idx >= 0 ? idx : 0;
    setFocusIndex(safeIdx);
    /** preventScroll throughout: scroll now closes this popover, so a focus call
     * that nudged an ancestor would dismiss it the instant it opened. */
    setTimeout(() => chipRefs.current[safeIdx]?.focus({ preventScroll: true }), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const moveFocus = (delta) => {
    const next = (focusIndex + delta + SUPERSET_OPTIONS.length) % SUPERSET_OPTIONS.length;
    setFocusIndex(next);
    chipRefs.current[next]?.focus({ preventScroll: true });
  };

  /** normalizeSupersetColor migrates colours the backend still sends from an
   * older palette, so a legacy value doesn't render as a colour we no longer
   * use anywhere else. */
  const swatchColor = normalizeSupersetColor(
    groupingColorCode || SUPERSET_DEFAULTS[groupingLetter],
  );

  return (
    <SupersetWrapper>
      <SupersetButton
        ref={buttonRef}
        type='button'
        aria-haspopup='listbox'
        aria-expanded={isOpen}
        aria-label={groupingLetter
          ? `${triggerAriaLabel} ${groupingLetter}, change`
          : `Select ${triggerAriaLabel.toLowerCase()}`}
        aria-keyshortcuts='A-Z'
        title='Type a letter (A–Z) to set a superset, or Backspace to clear'
        onClick={() => setIsOpen((v) => !v)}
        onKeyDown={(e) => {
          /** An open menu owns the keyboard: focus can still sit on this trigger
           * after the coach mouse-opens another menu, and the same key would
           * otherwise open (or type-ahead into) this picker too. */
          if (hasArrowKeyOwner()) return;
          if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsOpen(true);
            return;
          }
          /** Type a letter to pick without opening the popover. */
          if (selectByKey(e)) {
            e.preventDefault();
          }
        }}
      >
        {groupingLetter ? (
          <SupersetSwatch background={hexToRgba(swatchColor, 0.2)}>
            {groupingLetter}
          </SupersetSwatch>
        ) : (
          <SupersetEmptyHint>None</SupersetEmptyHint>
        )}
        <IcomoonReact iconSet={iconSet} size={9} icon='down-arrow' color={colors.textSecondary} />
      </SupersetButton>
      {isOpen && createPortal(
        <SupersetPopover
          ref={popoverRef}
          className='transposed-superset-popover'
          role='listbox'
          aria-label='Superset grouping'
          style={{
            top: popoverPos?.top ?? -9999,
            left: popoverPos?.left ?? -9999,
            visibility: popoverPos ? 'visible' : 'hidden',
          }}
          onKeyDown={(e) => {
            const step = SUPERSET_ARROW_STEPS[e.key];
            if (step !== undefined) {
              e.preventDefault();
              moveFocus(step);
              return;
            }
            /** Type-ahead: a letter key picks that grouping directly. */
            if (selectByKey(e)) {
              e.preventDefault();
            }
          }}
        >
          {SUPERSET_OPTIONS.map((opt, idx) => {
            const isSelected = groupingLetter === opt.letter;
            const bg = opt.color ? hexToRgba(normalizeSupersetColor(opt.color), 0.2) : undefined;
            return (
              <SupersetChip
                key={opt.letter || 'none'}
                type='button'
                role='option'
                aria-selected={isSelected}
                isSelected={isSelected}
                background={bg}
                muted={!opt.letter}
                tabIndex={focusIndex === idx ? 0 : -1}
                ref={(el) => { chipRefs.current[idx] = el; }}
                onClick={() => choose(opt.letter, opt.color)}
                onFocus={() => setFocusIndex(idx)}
              >
                {opt.letter || '—'}
              </SupersetChip>
            );
          })}
        </SupersetPopover>,
        document.body,
      )}
    </SupersetWrapper>
  );
};

InlineSupersetPicker.propTypes = {
  groupingLetter: PropTypes.string,
  groupingColorCode: PropTypes.string,
  /** (letter, color) — null/null when cleared. The host owns persistence. */
  onChoose: PropTypes.func.isRequired,
  triggerAriaLabel: PropTypes.string,
};

InlineSupersetPicker.defaultProps = {
  groupingLetter: null,
  groupingColorCode: null,
  triggerAriaLabel: 'Superset',
};

export default InlineSupersetPicker;
